import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ProductBreadcrumb,
  ProductHero,
  ProductGallery,
  QuickSpecsTabs,
  KeyFeatures,
  OptionalAccessories,
  Applications,
  WhyChooseLiftile,
  QualitySafetyCommitment,
  DownloadBrochure,
  PremiumFAQ,
  RelatedProducts,
  ProductFinalCTA,
} from "../components/product";
import {
  Anchor,
  ArrowUp,
  Award,
  BadgeCheck,
  Boxes,
  Building2,
  ClipboardCheck,
  Cog,
  CircleDot,
  Container,
  Droplets,
  Factory,
  Flag,
  Frame,
  Fuel,
  Gauge,
  HardHat,
  Home,
  Landmark,
  Layers,
  Link2,
  Package,
  Route,
  Ruler,
  ShieldCheck,
  Sparkles,
  Timer,
  TrendingUp,
  Wrench,
  Zap,
} from "lucide-react";

/* =====================================================================
   CONCRETE MIXER (LCM Series — LCM250 / LCM350 / LCM450) — dedicated
   product page. Composed entirely from /components/product/* shared
   primitives. Only product-specific data lives in this file.
   ===================================================================== */

const HERO_IMAGE = "/product-concrete-mixer.jpg";

const HERO_SPECS = [
  { Icon: Container, value: "Up to 450 L", label: "Drum Capacity" },
  { Icon: Package, value: "Up to 350 L", label: "Mixed Capacity" },
  { Icon: TrendingUp, value: "Up to 7 m³/hr", label: "Output" },
  { Icon: Flag, value: "Made in India", label: "Engineered & Manufactured" },
];

const HERO_BADGES = [
  "Heavy Duty Construction",
  "Diesel & Electric Options",
  "Owner Inspected",
  "Made in India",
];

const GALLERY = [
  {
    src: "/product-concrete-mixer.jpg",
    caption:
      "LIFTILE LCM Series Concrete Mixer — reliable, uniform batch mixing for every site",
  },
];

const QUICK_SPECS = [
  { label: "Drum Capacity", value: "250 – 450 L" },
  { label: "Mixed Capacity", value: "150 – 350 L" },
  { label: "Drum Speed", value: "20 – 22 RPM" },
  { label: "Output", value: "3 – 7 m³/hr" },
];

const MODELS = {
  LCM250: {
    name: "LCM250 — Compact Series",
    tagline:
      "Compact 250 L mixer for residential builds and small contractor sites",
    rows: [
      { label: "Drum Capacity", value: "250 Litres" },
      { label: "Mixed Capacity", value: "150 Litres" },
      {
        label: "Prime Mover",
        value: "5 HP Diesel Engine / 3 HP Single Phase Electric Motor",
      },
      { label: "Drum Speed", value: "22 RPM" },
      { label: "Transmission", value: "Heavy Duty Belt Drive" },
      { label: "Output", value: "3 – 4 m³/hr" },
    ],
  },
  LCM350: {
    name: "LCM350 — Contractor Series",
    tagline:
      "Versatile 350 L contractor-grade mixer for commercial and mid-scale sites",
    rows: [
      { label: "Drum Capacity", value: "350 Litres" },
      { label: "Mixed Capacity", value: "250 Litres" },
      {
        label: "Prime Mover",
        value: "6 HP Diesel Engine / 5 HP Electric Motor",
      },
      { label: "Drum Speed", value: "21 RPM" },
      { label: "Transmission", value: "Heavy Duty Roller Chain Drive" },
      { label: "Output", value: "4 – 5 m³/hr" },
    ],
  },
  LCM450: {
    name: "LCM450 — Pro Series",
    tagline:
      "Heavy-duty 450 L flagship for infrastructure, precast and high-output projects",
    rows: [
      { label: "Drum Capacity", value: "450 Litres" },
      { label: "Mixed Capacity", value: "350 Litres" },
      {
        label: "Prime Mover",
        value: "7 HP Diesel Engine / 5 HP Three Phase Electric Motor",
      },
      { label: "Drum Speed", value: "20 RPM" },
      { label: "Transmission", value: "Heavy Duty Roller Chain Drive" },
      { label: "Output", value: "6 – 7 m³/hr" },
    ],
  },
};

const FEATURES = [
  {
    Icon: Layers,
    title: "Heavy Duty Fabricated Steel Structure",
    desc: "Precision-welded structural steel chassis engineered for demanding site conditions, high vibration loads and long service life.",
  },
  {
    Icon: Container,
    title: "Precision Balanced Mixing Drum",
    desc: "Dynamically balanced drum profile ensures smooth, wobble-free rotation and consistent, uniform concrete mixing every cycle.",
  },
  {
    Icon: Cog,
    title: "Heavy Duty Replaceable Mixing Blades",
    desc: "Hardened, wear-resistant mixing blades bolted into the drum — easily replaced in the field to maintain mixing efficiency.",
  },
  {
    Icon: CircleDot,
    title: "Premium Bearings & Bushes",
    desc: "Trusted branded bearings and bushes deliver smooth rotation, low friction and dependable long-term drum performance.",
  },
  {
    Icon: Link2,
    title: "Heavy Duty Roller Chain / Belt Drive",
    desc: "Robust transmission — belt drive on LCM250 and heavy-duty roller chain drive on LCM350 and LCM450 for continuous industrial duty.",
  },
  {
    Icon: TrendingUp,
    title: "Smooth Tilting Mechanism",
    desc: "Ergonomically balanced hand-operated tilting system enables fast, low-effort concrete discharge into wheel barrows and buckets.",
  },
  {
    Icon: Sparkles,
    title: "Corrosion Resistant Powder Coated Finish",
    desc: "7-tank pre-treatment with premium powder coating for site-grade corrosion resistance and a lasting professional appearance.",
  },
  {
    Icon: Route,
    title: "Pneumatic or Steel Wheel Options",
    desc: "Choose from pneumatic tyres for smooth site relocation or heavy-duty steel wheels for permanent installation locations.",
  },
  {
    Icon: Fuel,
    title: "Diesel & Electric Drive Options",
    desc: "Every LCM Series model is available with either a diesel engine or an electric motor — pick what best suits your site power availability.",
  },
  {
    Icon: Wrench,
    title: "Easy Maintenance Design",
    desc: "Simple mechanical design with easy access to bearings, drive components and drum service points keeps downtime low.",
  },
  {
    Icon: ShieldCheck,
    title: "Reliable On-Site Performance",
    desc: "Engineered for continuous daily use — dependable batch after batch, project after project, on demanding construction sites.",
  },
  {
    Icon: ClipboardCheck,
    title: "Owner Inspected Before Dispatch",
    desc: "Every LCM Series mixer is personally inspected and quality signed off before it leaves the LIFTILE factory.",
  },
];

const ACCESSORIES = [
  {
    Icon: Fuel,
    title: "Dual Drive System (Diesel + Electric)",
    desc: "Combined diesel engine and electric motor package — switch power source based on site availability without downtime.",
  },
  {
    Icon: Droplets,
    title: "Water Pump Kit",
    desc: "Integrated water pump for automatic water addition — improves batch consistency and reduces manual site handling.",
  },
  {
    Icon: Gauge,
    title: "Digital Water Meter",
    desc: "Precision digital water metering for accurate, repeatable water-to-cement ratios on every concrete batch.",
  },
  {
    Icon: Anchor,
    title: "Heavy Duty Towing Bar",
    desc: "Reinforced towing bar for safe, quick relocation of the mixer between site positions and between projects.",
  },
  {
    Icon: Route,
    title: "Heavy Duty Pneumatic Wheels",
    desc: "Upgraded pneumatic tyre kit for smooth mobility across uneven site terrain and rough construction ground.",
  },
  {
    Icon: BadgeCheck,
    title: "Stainless Steel Drum",
    desc: "Corrosion-resistant stainless steel drum option for demanding marine, chemical or aggressive environment projects.",
  },
  {
    Icon: Frame,
    title: "Industrial Foundation Mounting Kit",
    desc: "Bolt-down foundation mounting kit for permanent installation in precast yards, RMC facilities and central batching plants.",
  },
];

const APPLICATIONS = [
  {
    Icon: Home,
    title: "Residential Construction",
    desc: "Consistent site-mixed concrete for individual homes, villas, apartments and residential tower projects.",
  },
  {
    Icon: Building2,
    title: "Commercial Buildings",
    desc: "Dependable batch mixing for offices, malls, hotels, hospitals and mixed-use commercial developments.",
  },
  {
    Icon: Factory,
    title: "Industrial Projects",
    desc: "Robust performance on plants, factories, warehouses and heavy industrial construction sites.",
  },
  {
    Icon: Landmark,
    title: "Infrastructure Development",
    desc: "Reliable site concrete for metros, treatment plants, power plants, ports and public infrastructure works.",
  },
  {
    Icon: Layers,
    title: "RCC Works",
    desc: "Uniform mixing for reinforced concrete columns, beams, slabs and structural elements across every floor.",
  },
  {
    Icon: Anchor,
    title: "Foundations",
    desc: "Consistent, high-strength concrete for footings, plinths, rafts and deep foundation work.",
  },
  {
    Icon: Ruler,
    title: "Flooring",
    desc: "Smooth, workable batches for industrial, commercial and residential concrete flooring projects.",
  },
  {
    Icon: Route,
    title: "Road Construction",
    desc: "Dependable batch mixing for concrete roads, kerbs, drains and highway construction supporting works.",
  },
  {
    Icon: Boxes,
    title: "Precast Construction",
    desc: "Consistent concrete output for precast yards, formwork factories and modular building components.",
  },
];

const TRUST_CARDS = [
  {
    Icon: HardHat,
    title: "Heavy Duty Construction",
    desc: "Precision-welded structural steel chassis built for demanding sites and continuous industrial duty.",
  },
  {
    Icon: Container,
    title: "Uniform Concrete Mixing",
    desc: "Precision-balanced drum and hardened mixing blades deliver consistent, workable concrete every batch.",
  },
  {
    Icon: Award,
    title: "Premium Components",
    desc: "Trusted branded engines, motors, bearings and drive components from leading Indian and global brands.",
  },
  {
    Icon: ClipboardCheck,
    title: "Owner Inspected",
    desc: "Every LCM Series mixer is personally inspected and quality signed off before dispatch.",
  },
  {
    Icon: Wrench,
    title: "Low Maintenance",
    desc: "Simple, service-friendly mechanical design with easy access to drive and drum components keeps costs low.",
  },
  {
    Icon: Flag,
    title: "Made in India",
    desc: "Designed and manufactured in India for Indian construction site conditions and after-sales support.",
  },
  {
    Icon: ShieldCheck,
    title: "Reliable Site Performance",
    desc: "Trial-run tested for dependable daily batch mixing — project after project, site after site.",
  },
];

const TECH_HIGHLIGHTS = [
  {
    Icon: Container,
    title: "Heavy Duty Mixing Drum",
    desc: "Precision-balanced, heavy-gauge steel drum with hardened replaceable mixing blades for uniform concrete every cycle.",
  },
  {
    Icon: TrendingUp,
    title: "Smooth Tilting Mechanism",
    desc: "Ergonomically balanced hand-operated tilting system for fast, low-effort concrete discharge on site.",
  },
  {
    Icon: Link2,
    title: "Premium Drive System",
    desc: "Heavy-duty belt drive (LCM250) and roller chain drive (LCM350 / LCM450) engineered for continuous industrial duty.",
  },
  {
    Icon: Cog,
    title: "Uniform Mixing",
    desc: "Optimized drum geometry and blade profile deliver consistent, workable batches across every discharge.",
  },
  {
    Icon: Wrench,
    title: "Low Maintenance",
    desc: "Service-friendly mechanical design with accessible bearings, drive components and drum service points.",
  },
  {
    Icon: Timer,
    title: "Long Service Life",
    desc: "Premium materials, powder-coated corrosion protection and branded components ensure years of dependable use.",
  },
];

const FAQS = [
  {
    q: "Which LCM Series model should I choose?",
    a: "The right model depends on your project size and daily concrete demand.\n\nLCM250 (Compact Series) — best for individual homes, small residential builds and light contractor work (3–4 m³/hr).\n\nLCM350 (Contractor Series) — the everyday workhorse for commercial buildings, mid-scale contractor sites and general RCC work (4–5 m³/hr).\n\nLCM450 (Pro Series) — heavy-duty flagship for infrastructure projects, precast yards, high-rise construction and continuous daily production (6–7 m³/hr).",
  },
  {
    q: "Should I choose diesel or electric operation?",
    a: "Every LCM Series model is available with either a diesel engine or an electric motor.\n\nChoose diesel if your site does not have reliable three-phase power or you need full mobility across a large site.\n\nChoose electric if reliable power supply is available — it delivers lower running costs, cleaner operation and less noise.\n\nYou can also opt for our Dual Drive System accessory to switch between diesel and electric power based on site availability.",
  },
  {
    q: "What is the mixing capacity of the LCM Series?",
    a: "The LCM Series delivers mixed concrete output ranging from 150 litres per batch (LCM250) up to 350 litres per batch (LCM450).\n\nHourly output ranges from 3 m³/hr on the Compact Series to 7 m³/hr on the Pro Series depending on batch cycle time, mix design and site handling.",
  },
  {
    q: "What maintenance does the LCM Series require?",
    a: "The LCM Series uses a simple, low-maintenance mechanical design. Routine tasks include periodic drum cleaning, lubrication of bearings and drive components, inspection of mixing blades for wear, and checking the belt or roller chain tension.\n\nDiesel engines and electric motors follow the standard OEM service schedules from their respective manufacturers.",
  },
  {
    q: "Are spare parts easily available?",
    a: "Yes.\n\nLIFTILE maintains ready inventory of genuine LCM Series spare parts including mixing blades, bearings, drive belts, roller chains, drum liners, engine and motor components. Contact our spares team to source what you need for your machine.",
  },
  {
    q: "Does LIFTILE provide a warranty on the Concrete Mixer?",
    a: "Yes.\n\nEvery LIFTILE LCM Series mixer is backed by our standard manufacturer's warranty on structural components, drum, drive system and electricals. Contact our sales team for the current warranty terms applicable to your project and region.",
  },
  {
    q: "Which construction sites is the LCM Series suitable for?",
    a: "The LCM Series is engineered for a wide range of construction environments including residential construction, commercial buildings, industrial projects, infrastructure development, RCC works, foundations, flooring, road construction and precast construction.",
  },
  {
    q: "Is service and support available across India?",
    a: "Yes.\n\nLIFTILE provides installation guidance, spare parts, technical assistance and after-sales service across India through our nationwide dealer and support network.",
  },
];

const RELATED_PRODUCTS = [
  {
    slug: "reversible-drum-concrete-mixer",
    name: "Reversible Drum Concrete Mixer",
    desc: "LRDM Series high-output reversible drum mixer with hopper-fed loading for infrastructure and precast projects.",
    image: "/product-reversible-drum-mixer.jpg",
  },
  {
    slug: "monkey-lift",
    name: "Monkey Lift",
    desc: "Compact LML250 mini material lifter — the perfect partner for moving mixed concrete to upper floors on tight sites.",
    image: "/product-monkey-lift.jpg",
  },
  {
    slug: "wheel-barrow",
    name: "Wheel Barrow",
    desc: "Heavy-duty single and double wheel material transport barrows for smooth on-site concrete handling.",
    image: "/product-wheel-barrow.jpg",
  },
];

const CTA_BADGES = [
  "Heavy Duty Construction",
  "Diesel & Electric Options",
  "Owner Inspected",
  "Made in India",
];

/* ---------- Blueprint SVG for Download Brochure ---------- */
const LCM_BLUEPRINT_SVG = (
  <svg
    aria-hidden
    viewBox="0 0 400 400"
    className="absolute right-0 top-1/2 -translate-y-1/2 w-[280px] md:w-[360px] h-auto opacity-[0.14] pointer-events-none"
    fill="none"
    stroke="#FF6B1A"
    strokeWidth="1.2"
  >
    {/* Ground line */}
    <line
      x1="30"
      y1="345"
      x2="380"
      y2="345"
      strokeDasharray="4 4"
      opacity="0.5"
    />

    {/* Chassis / A-frame (left leg) */}
    <line x1="80" y1="345" x2="180" y2="200" />
    <line x1="150" y1="345" x2="180" y2="200" />
    {/* Chassis / A-frame (right leg) */}
    <line x1="320" y1="345" x2="220" y2="200" />
    <line x1="260" y1="345" x2="220" y2="200" />
    {/* Chassis cross beam */}
    <line x1="115" y1="290" x2="290" y2="290" />
    {/* Base towbar */}
    <line x1="30" y1="330" x2="90" y2="345" />
    <circle cx="30" cy="330" r="4" />

    {/* Wheels */}
    <circle cx="110" cy="340" r="20" />
    <circle cx="110" cy="340" r="8" />
    <circle cx="290" cy="340" r="20" />
    <circle cx="290" cy="340" r="8" />
    {[0, 1, 2, 3, 4, 5].map((i) => {
      const a = (i * Math.PI) / 3;
      return (
        <g key={i} opacity="0.55">
          <line
            x1={110 + Math.cos(a) * 8}
            y1={340 + Math.sin(a) * 8}
            x2={110 + Math.cos(a) * 20}
            y2={340 + Math.sin(a) * 20}
          />
          <line
            x1={290 + Math.cos(a) * 8}
            y1={340 + Math.sin(a) * 8}
            x2={290 + Math.cos(a) * 20}
            y2={340 + Math.sin(a) * 20}
          />
        </g>
      );
    })}

    {/* Drum body (tilted egg shape) */}
    <ellipse cx="200" cy="150" rx="110" ry="85" />
    {/* Drum discharge opening (front lip) */}
    <ellipse cx="200" cy="105" rx="42" ry="14" strokeDasharray="3 3" />
    {/* Drum bands */}
    <path d="M 100 165 Q 200 200 300 165" opacity="0.6" />
    <path d="M 105 130 Q 200 100 295 130" opacity="0.6" />

    {/* Internal mixing blades (indicative) */}
    <line
      x1="150"
      y1="160"
      x2="200"
      y2="140"
      strokeDasharray="2 3"
      opacity="0.7"
    />
    <line
      x1="250"
      y1="160"
      x2="200"
      y2="140"
      strokeDasharray="2 3"
      opacity="0.7"
    />

    {/* Drive axle from motor to drum */}
    <line x1="335" y1="230" x2="270" y2="200" strokeWidth="2" />
    {/* Motor / gearbox housing */}
    <rect x="330" y="220" width="45" height="35" />
    <circle cx="352" cy="237" r="8" />
    {/* Belt / chain guard shape (small) */}
    <rect
      x="270"
      y="195"
      width="20"
      height="15"
      strokeDasharray="3 2"
      opacity="0.6"
    />

    {/* Tilt handwheel (left side of drum) */}
    <circle cx="105" cy="200" r="22" />
    <circle cx="105" cy="200" r="6" />
    {[0, 1, 2, 3].map((i) => {
      const a = (i * Math.PI) / 2;
      return (
        <line
          key={i}
          x1={105 + Math.cos(a) * 6}
          y1={200 + Math.sin(a) * 6}
          x2={105 + Math.cos(a) * 22}
          y2={200 + Math.sin(a) * 22}
          opacity="0.6"
        />
      );
    })}
    {/* Tilt link to drum */}
    <line
      x1="127"
      y1="195"
      x2="150"
      y2="180"
      strokeDasharray="2 2"
      opacity="0.6"
    />

    {/* Dimension callout */}
    <line
      x1="90"
      y1="60"
      x2="310"
      y2="60"
      strokeDasharray="3 3"
      opacity="0.55"
    />
    <line x1="90" y1="55" x2="90" y2="65" opacity="0.6" />
    <line x1="310" y1="55" x2="310" y2="65" opacity="0.6" />
    <text
      x="180"
      y="50"
      fill="#FF6B1A"
      fontSize="10"
      fontFamily="Montserrat"
      opacity="0.7"
    >
      Ø 450 L
    </text>

    {/* Labels */}
    <text
      x="150"
      y="255"
      fill="#FF6B1A"
      fontSize="9"
      fontFamily="Montserrat"
      opacity="0.65"
    >
      DRUM
    </text>
    <text
      x="60"
      y="235"
      fill="#FF6B1A"
      fontSize="9"
      fontFamily="Montserrat"
      opacity="0.65"
    >
      TILT
    </text>
    <text
      x="325"
      y="275"
      fill="#FF6B1A"
      fontSize="9"
      fontFamily="Montserrat"
      opacity="0.65"
    >
      DRIVE
    </text>
  </svg>
);

export default function ConcreteMixerPage() {
  return (
    <>
      <Navbar />
      <ProductBreadcrumb productName="Concrete Mixer (LCM Series)" />

      {/* ─── SECTION 1: HERO ─── */}
      <ProductHero
        testId="lcm-hero"
        backgroundImage={HERO_IMAGE}
        eyebrow="LCM Series"
        title={
          <>
            LIFTILE
            <br />
            Concrete <span className="text-liftile-orange">Mixer</span>
          </>
        }
        subtitle="Engineered for reliable, efficient and uniform concrete mixing across residential, commercial and infrastructure projects. Built with heavy-duty construction, premium components and a low-maintenance design for long-lasting site performance."
        models={Object.keys(MODELS)}
        primaryCta={{ label: "Request a Quote", href: "/contact" }}
        secondaryCta={{ label: "Download Brochure" }}
        specs={HERO_SPECS}
        badges={HERO_BADGES}
      />

      {/* ─── SECTION 2: GALLERY ─── */}
      <ProductGallery
        testId="lcm-gallery"
        heading={
          <>
            LCM Series <span className="text-liftile-orange">In Action</span>
          </>
        }
        subtitle="A closer look at the LIFTILE LCM Series Concrete Mixer — from precision fabrication at our factory to real construction sites across India."
        items={GALLERY}
        seriesLabel="LCM Series"
        imageAlt="LIFTILE LCM Series Concrete Mixer"
      />

      {/* ─── SECTION 3: QUICK SPECS (split layout with 3 model tabs) ─── */}
      <QuickSpecsTabs
        testId="lcm-quick-specs"
        heading={
          <>
            Quick <span className="text-liftile-orange">Specifications</span>
          </>
        }
        subtitle="The essentials — with a model selector to switch between LCM250, LCM350 and LCM450 configurations."
        quickSpecs={QUICK_SPECS}
        models={MODELS}
        defaultModel="LCM350"
        footerNote={null}
      />

      {/* ─── SECTION 4: KEY FEATURES ─── */}
      <KeyFeatures
        testId="lcm-features"
        heading={
          <>
            Built for Site Productivity.{" "}
            <br className="hidden sm:block" />
            <span className="text-liftile-orange">
              Engineered for Uniform Mixing.
            </span>
          </>
        }
        subtitle="Every LIFTILE LCM Series Concrete Mixer is engineered with premium branded components and rigorous quality inspections to deliver dependable batch after batch on demanding construction sites."
        items={FEATURES}
      />

      {/* ─── SECTION 5: OPTIONAL ACCESSORIES ─── */}
      <OptionalAccessories
        testId="lcm-accessories"
        heading={
          <>
            Optional <span className="text-liftile-orange">Accessories</span>
          </>
        }
        subtitle="Customize the LCM Series for your specific site conditions and project requirements with our range of purpose-built accessories."
        items={ACCESSORIES}
        gridCols="lg:grid-cols-4"
      />

      {/* ─── SECTION 6: APPLICATIONS ─── */}
      <Applications
        testId="lcm-applications"
        heading={
          <>
            Ideal for Every{" "}
            <span className="text-liftile-orange">Construction Project</span>
          </>
        }
        subtitle="From residential builds to road construction, foundations and precast yards — the LCM Series delivers consistent site concrete across every construction environment."
        items={APPLICATIONS}
        variant="dark"
        gridCols="lg:grid-cols-3"
      />

      {/* ─── SECTION 7: WHY CHOOSE LIFTILE ─── */}
      <WhyChooseLiftile
        testId="lcm-why-choose"
        image="/product-concrete-mixer.jpg"
        imageAlt="LIFTILE LCM Series Concrete Mixer — reliable batch mixing performance"
        imageCaption="LCM Series — uniform mixing, heavy-duty build, dependable daily site productivity."
        floatingBadge={{ top: "Up to", bottom: "7 m³/hr" }}
        eyebrow="Why LCM Series"
        heading={
          <>
            Built for Heavy Duty{" "}
            <br className="hidden sm:block" />
            <span className="text-liftile-orange">Site Concrete</span>
          </>
        }
        subtitle="The LCM Series combines heavy-duty construction, uniform mixing performance and premium branded components to deliver dependable batch concrete on every construction project."
        trustCards={TRUST_CARDS}
      />

      {/* ─── SECTION 8: TECHNICAL HIGHLIGHTS ─── */}
      <QualitySafetyCommitment
        testId="lcm-tech-highlights"
        eyebrow="Technical Highlights"
        heading={
          <>
            Precision Engineering.{" "}
            <span className="text-liftile-orange">Industrial Grade Build.</span>
          </>
        }
        subtitle="A closer look at the engineering foundations that make the LCM Series a reliable, uniform and long-lasting concrete mixer for modern construction sites."
        items={TECH_HIGHLIGHTS}
      />

      {/* ─── SECTION 9: DOWNLOAD BROCHURE ─── */}
      <DownloadBrochure
        testId="lcm-brochure"
        heading={
          <>
            Download Product{" "}
            <span className="text-liftile-orange">Brochure</span>
          </>
        }
        subtitle="Download the complete LCM Series technical catalogue including specifications, drawings, features and product information for LCM250, LCM350 and LCM450 configurations."
        blueprintSvg={LCM_BLUEPRINT_SVG}
      />

      {/* ─── SECTION 10: FAQ ─── */}
      <PremiumFAQ
        testId="lcm-faq"
        heading={
          <>
            Frequently Asked{" "}
            <span className="text-liftile-orange">Questions</span>
          </>
        }
        subtitle="Everything builders, contractors and site engineers most often ask us about the LIFTILE LCM Series Concrete Mixer."
        items={FAQS}
      />

      {/* ─── SECTION 11: RELATED PRODUCTS ─── */}
      <RelatedProducts
        testId="lcm-related"
        heading={
          <>
            Explore More{" "}
            <span className="text-liftile-orange">LIFTILE Solutions</span>
          </>
        }
        items={RELATED_PRODUCTS}
      />

      {/* ─── SECTION 12: FINAL CTA ─── */}
      <ProductFinalCTA
        testId="lcm-product-cta"
        backgroundImage="/project-pandesara-surat.png"
        backgroundAlt="LIFTILE LCM Series Concrete Mixer on a live construction site"
        heading={
          <>
            Reliable Mixing.{" "}
            <span className="text-liftile-orange">Every Batch.</span>
          </>
        }
        subtitle="The LIFTILE LCM Series Concrete Mixers are designed to deliver consistent mixing performance, heavy-duty durability and dependable operation for every construction project."
        primaryCta={{ label: "Request a Quote", href: "/contact" }}
        secondaryCta={{ label: "Contact Sales Team", href: "/contact" }}
        trustBadges={CTA_BADGES}
      />

      <Footer />
    </>
  );
}
